import React, { useEffect, useMemo, useState } from "react";
import { getFirestore, doc, onSnapshot } from "firebase/firestore";
import { useParams, useNavigate } from "react-router-dom";

import { deleteApprovedPost } from "./blogFirebase";
import ConfirmModal from "./ConfirmModal";
import BlogPostBody from "./BlogPostBody";
import { resolveContentHtml } from "./postContent";

const db = getFirestore();

const AdminPublishedPost = () => {
  const { docId }  = useParams();
  const navigate   = useNavigate();

  const [post, setPost]         = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);

  const [showImgPrompt, setShowImgPrompt]                   = useState(false);
  const [showSecondaryImgPrompt, setShowSecondaryImgPrompt] = useState(false);

  // Subscribe to the published doc
  useEffect(() => {
    const ref   = doc(db, "blog_posts", docId);
    const unsub = onSnapshot(ref, (snap) => {
      if (!snap.exists()) {
        setNotFound(true);
        setPost(null);
      } else {
        setPost({ id: snap.id, ...snap.data() });
        setNotFound(false);
      }
    }, (err) => {
      console.error("AdminPublishedPost snapshot error:", err);
      setNotFound(true);
    });
    return () => unsub();
  }, [docId]);

  const contentHtml = useMemo(() => resolveContentHtml(post), [post]);

  const openDeleteConfirm = () => {
    setConfirmModal({
      title: "Delete Published Post",
      message: `"${post.title}" will be permanently deleted from the live blog. This cannot be undone.`,
      confirmLabel: "🗑 Delete",
      confirmClass: "bg-red-600 hover:bg-red-700",
      onConfirm: doDelete,
    });
  };

  const doDelete = async () => {
    setConfirmModal(null);
    setDeleting(true);
    try {
      await deleteApprovedPost({ docId });
      navigate("/blog");
    } catch (e) {
      console.error("deleteBlogPost error:", e);
      setDeleting(false);
    }
  };

  // ── Render states ────────────────────────────────────────────────────────────
  if (!post && !notFound) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="text-xl text-gray-500">Loading post…</div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="text-center py-16 bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="text-5xl mb-4">🔍</div>
        <p className="text-gray-500 text-lg">Post not found.</p>
        <button
          onClick={() => navigate("/blog")}
          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-sm"
        >
          ← Back to list
        </button>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-gray-50 -mx-4 -mt-6">
      {confirmModal && (
        <ConfirmModal
          title={confirmModal.title}
          message={confirmModal.message}
          confirmLabel={confirmModal.confirmLabel}
          confirmClass={confirmModal.confirmClass}
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal(null)}
        />
      )}

      {/* Action bar */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => navigate("/blog")}
          className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 font-medium"
        >
          ← Back
        </button>

        <span className="text-gray-300">|</span>

        <span className="px-2.5 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
          ✅ Published
        </span>

        <div className="flex-1" />

        <button
          onClick={openDeleteConfirm}
          disabled={deleting}
          className="px-3 py-1.5 text-sm font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {deleting ? "Deleting…" : "🗑 Delete"}
        </button>
      </div>

      <BlogPostBody
        post={post}
        contentHtml={contentHtml}
        showImgPrompt={showImgPrompt}
        onToggleImgPrompt={() => setShowImgPrompt(v => !v)}
        showSecondaryImgPrompt={showSecondaryImgPrompt}
        onToggleSecondaryImgPrompt={() => setShowSecondaryImgPrompt(v => !v)}
      />
    </div>
  );
};

export default AdminPublishedPost;
